import React from 'react';
import { useSimulation } from '../context/SimulationContext';
import { X, AlertTriangle, MessageCircle, Info } from 'lucide-react';
import { COACH_TRIGGERS } from '../data/engineeringNodeTable';

// Coach nudge messages mapped to triggers
const NUDGE_MESSAGES = {
  [COACH_TRIGGERS.INTENT_MISMATCH_OPENING]: {
    title: 'Your choice is drifting from your plan',
    message: 'Your opening contradicts your stated plan. You planned to lead with inquiry, but this choice frames the conversation as complaint-driven.',
    type: 'warning',
  },
  [COACH_TRIGGERS.SOFT_MISMATCH_LOG]: {
    title: 'Approach Drift',
    message: 'This response softens your intended approach. While not necessarily off-track, it may leave clarity under-defined.',
    type: 'info',
  },
  [COACH_TRIGGERS.HARD_MISMATCH_NUDGE]: {
    title: 'This move undercuts your stated approach',
    message: 'This choice significantly deviates from your stated intent and may damage trust. Consider whether this aligns with your goals.',
    type: 'error',
  },
  [COACH_TRIGGERS.SKIPPED_INTENDED_INQUIRY]: {
    title: 'You moved to the issue before gathering enough context',
    message: 'You intended to understand the teacher\'s account first, but this response names the issue without gathering context.',
    type: 'warning',
  },
  [COACH_TRIGGERS.SOFT_MISMATCH_TONE]: {
    title: 'Tone Shift',
    message: 'Your tone is more neutral than your empathetic intent suggests. This may feel distant to Ms. Ortega.',
    type: 'info',
  },
  [COACH_TRIGGERS.SOFT_MISMATCH_SUPPORT_BALANCE]: {
    title: 'Support Balance',
    message: 'This response leans more directive than supportive. Consider how this lands given your intent.',
    type: 'info',
  },
  [COACH_TRIGGERS.SOFT_MISMATCH_CONTEXT_SPECIFICITY]: {
    title: 'Context Specificity',
    message: 'You intended to explore family history, but this response doesn\'t specifically address that context.',
    type: 'info',
  },
  [COACH_TRIGGERS.COACH_NUDGE_SOFT_PATTERN]: {
    title: 'Pattern Alert',
    message: 'Multiple soft mismatches in this stage. Your approach may be drifting from your intended strategy.',
    type: 'warning',
  },
  [COACH_TRIGGERS.AVOIDED_NAMED_PRIORITY]: {
    title: 'You\'re leaving one of your stated priorities under-addressed',
    message: 'You identified student impact as a priority not to avoid, but this response keeps accountability vague.',
    type: 'warning',
  },
  [COACH_TRIGGERS.EQUITY_LENS_NOT_APPLIED]: {
    title: 'You planned to consider context, but this move doesn\'t do that yet',
    message: 'You selected the equity check, but this choice doesn\'t explore context or family history.',
    type: 'warning',
  },
  [COACH_TRIGGERS.SATISFIES_EQUITY_INTENT]: {
    title: 'This move reflects the context-aware approach you planned',
    message: 'This choice aligns with your equity lens by exploring context and family history.',
    type: 'success',
  },
};

export function CoachNudge() {
  const { state, dismissNudge } = useSimulation();
  const { activeNudge } = state;

  if (!activeNudge) return null;

  const nudgeConfig = NUDGE_MESSAGES[activeNudge.trigger] || {
    title: 'Coach Observation',
    message: activeNudge.message || 'Consider how this choice aligns with your intent.',
    type: 'info',
  };

  const getIcon = () => {
    switch (nudgeConfig.type) {
      case 'error':
        return <AlertTriangle className="w-5 h-5 text-[#DA291C]" strokeWidth={1.5} />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-[#FFB81C]" strokeWidth={1.5} />;
      case 'success':
        return <MessageCircle className="w-5 h-5 text-[#78BE20]" strokeWidth={1.5} />;
      default:
        return <Info className="w-5 h-5 text-[#00A9E0]" strokeWidth={1.5} />;
    }
  };

  const getBorderColor = () => {
    switch (nudgeConfig.type) {
      case 'error':
        return 'border-l-[#DA291C]';
      case 'warning':
        return 'border-l-[#FFB81C]';
      case 'success':
        return 'border-l-[#78BE20]';
      default:
        return 'border-l-[#00A9E0]';
    }
  };

  return (
    <div 
      className={`fixed right-2 sm:right-6 top-16 sm:top-24 w-[calc(100%-1rem)] sm:w-80 glass-panel-dark text-white p-4 sm:p-6 shadow-2xl z-50 coach-nudge-enter border-l-4 ${getBorderColor()}`}
      data-testid="coach-nudge"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          {getIcon()}
          <h4 className="font-headline font-semibold text-sm">
            {nudgeConfig.title}
          </h4>
        </div>
        <button 
          onClick={dismissNudge}
          className="p-1 hover:bg-white/10 rounded transition-colors"
          data-testid="coach-nudge-dismiss"
          aria-label="Dismiss coach nudge"
        >
          <X className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>

      {/* Message */}
      <p className="text-sm text-slate-200 leading-relaxed">
        {nudgeConfig.message}
      </p>

      {/* Footer */}
      <div className="mt-4 pt-4 border-t border-white/10">
        <p className="text-xs text-slate-400 uppercase tracking-wider">
          Coach Mode Active
        </p>
      </div>
    </div>
  );
}

// Coach Nudge History Panel
export function CoachNudgeHistory() {
  const { state } = useSimulation();
  const { coachNudges } = state;

  if (coachNudges.length === 0) {
    return (
      <div className="p-6 text-center" data-testid="coach-nudge-history-empty">
        <MessageCircle className="w-8 h-8 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
        <p className="text-sm text-slate-400">No coach observations yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="coach-nudge-history">
      <h4 className="text-xs font-semibold uppercase tracking-widest text-slate-400 px-4">
        Coach Observations
      </h4>
      
      {coachNudges.map((nudge, index) => {
        const config = NUDGE_MESSAGES[nudge.trigger] || { title: 'Observation', type: 'info' };
        
        return (
          <div 
            key={nudge.id || index}
            className={`p-4 bg-slate-50 border-l-2 ${
              config.type === 'error' ? 'border-l-[#DA291C]' :
              config.type === 'warning' ? 'border-l-[#FFB81C]' :
              config.type === 'success' ? 'border-l-[#78BE20]' :
              'border-l-[#00A9E0]'
            }`}
            data-testid={`coach-nudge-item-${index}`}
          >
            <p className="text-xs font-semibold text-[#002B45] mb-1">
              {config.title}
            </p>
            <p className="text-xs text-slate-500">
              {config.message}
            </p>
          </div>
        );
      })}
    </div>
  );
}

export default CoachNudge;
